import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import BASE_URL from '../../url';
import { 
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  CartesianGrid, Legend
} from 'recharts';
import { 
  TrendingUp, Users, Award, Briefcase, Building2, 
  Search, ChevronDown, X, Download, RefreshCw, Layers
} from 'lucide-react';
import '../../Submissions/styles/JobReport.css';
import '../../Submissions/styles/ManagerDashboard.css';
import { generateBenchReportHtml } from '../../Submissions/utils/reportExportUtils';

const COLORS = [
  '#3b5bfd', '#0284c7', '#7c3aed', '#db2777', '#ea580c', 
  '#2563eb', '#ca8a04', '#4f46e5', '#1d4ed8', '#e11d48'
];

// Helper to format date YYYY-MM-DD
const toYMD = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ─────────────────────────────────────────
// 10-Step Progress Chevron Bar
// ─────────────────────────────────────────
const ProgressChevron = ({ 
  counts = [], 
  rejectedCount = 0, 
  withdrawnCount = 0, 
  hiredCount = 0 
}) => {
  const stepsCount = 10;
  return (
    <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
      <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
        {[...Array(stepsCount)].map((_, index) => {
          const count = counts[index] || 0;
          const hasApps = count > 0;
          return (
            <div
              key={index}
              style={{
                padding: '2px 6px',
                borderRadius: '3px',
                fontSize: '10px',
                fontWeight: 700,
                background: hasApps ? '#3b5bfd' : '#e2e8f0',
                color: hasApps ? '#ffffff' : '#64748b',
                border: hasApps ? '1px solid #2563eb' : '1px solid #cbd5e1',
                minWidth: '18px',
                textAlign: 'center'
              }}
              title={`Step ${index + 1}: ${count} Active`}
            >
              {count}
            </div>
          );
        })}
      </div>

      {rejectedCount > 0 && (
        <span 
          style={{
            fontSize: '9.5px',
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: '10px',
            background: '#fee2e2',
            color: '#dc2626',
            border: '1px solid #fca5a5',
            whiteSpace: 'nowrap'
          }}
          title={`Rejected Candidates: ${rejectedCount}`}
        >
          ✕ {rejectedCount}
        </span>
      )}

      {hiredCount > 0 && (
        <span 
          style={{
            fontSize: '9.5px',
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: '10px',
            background: '#eff6ff',
            color: '#1d4ed8',
            border: '1px solid #bfdbfe',
            whiteSpace: 'nowrap'
          }}
          title={`Hired Candidates: ${hiredCount}`}
        >
          ✓ {hiredCount}
        </span>
      )}
    </div>
  );
};

// ─────────────────────────────────────────
// Searchable US Client Select Dropdown
// ─────────────────────────────────────────
const SearchableClientSelect = ({ clients = [], selectedClient, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredClients = clients.filter(c =>
    (c.clientName || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <label style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
        Client
      </label>
      <div
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '7px 12px', background: '#fff', border: '1px solid #cbd5e1',
          borderRadius: '6px', cursor: 'pointer', fontSize: '13px', color: '#1e293b',
          minWidth: '180px', height: '36px', boxSizing: 'border-box'
        }}
      >
        <span style={{ fontWeight: selectedClient ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedClient || 'All Clients'}
        </span>
        <ChevronDown size={14} color="#64748b" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginLeft: '8px', flexShrink: 0 }} />
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute', top: '100%', left: 0, marginTop: '4px',
            width: '240px', background: '#fff', border: '1px solid #cbd5e1',
            borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            zIndex: 99999, overflow: 'hidden'
          }}
        >
          <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px', background: '#f8fafc' }}>
            <Search size={14} color="#94a3b8" />
            <input
              type="text"
              autoFocus
              placeholder="Search client…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '12px', color: '#1e293b' }}
            />
            {search && <X size={12} color="#94a3b8" style={{ cursor: 'pointer' }} onClick={() => setSearch('')} />}
          </div>

          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <div
              onClick={() => { onSelect(null); setIsOpen(false); setSearch(''); }}
              style={{
                padding: '8px 12px', fontSize: '12px', cursor: 'pointer',
                background: !selectedClient ? '#eff6ff' : 'transparent',
                color: !selectedClient ? '#1d4ed8' : '#334155',
                fontWeight: !selectedClient ? 600 : 400
              }}
            >
              All Clients
            </div>
            {filteredClients.length === 0 ? (
              <div style={{ padding: '10px', fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>No clients found</div>
            ) : (
              filteredClients.map((c, i) => {
                const isSelected = selectedClient === c.clientName;
                return (
                  <div
                    key={i}
                    onClick={() => { onSelect(c.clientName); setIsOpen(false); setSearch(''); }}
                    style={{
                      padding: '8px 12px', fontSize: '12px', cursor: 'pointer',
                      background: isSelected ? '#eff6ff' : 'transparent',
                      color: isSelected ? '#1d4ed8' : '#334155',
                      fontWeight: isSelected ? 600 : 400
                    }}
                  >
                    {c.clientName}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────
// Submissions Modal (With All, Sourced, and Submitted Tabs)
// ─────────────────────────────────────────
const SubmissionsModal = ({ recruiter, startDate, endDate, client, onClose }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'sourced' | 'submitted'

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BASE_URL}/api/bench-manage/report/submissions`, {
          params: { recruiterId: recruiter.id, startDate, endDate, client: client || 'all' },
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          const mapped = (res.data.data || []).map(role => {
            let loc = role.location || 'N/A';
            if (loc !== 'N/A') {
              loc = loc.split(',').map(p => p.trim()).filter(p => p && p !== '.').join(', ');
            }
            return {
              id: role.id,
              jobId: `JOB-${String(role.id).padStart(5, '0')}`,
              externalJobRef: role.externalJobRef || role.jobId || 'N/A',
              clientName: role.clientName || 'N/A',
              jobTitle: role.jobTitle || 'N/A',
              candidateName: role.candidateName || 'Candidate',
              candidateEmail: role.candidateEmail || '',
              pipelineCategory: role.pipelineCategory || (role.currentStep < 8 ? 'SOURCED' : 'SUBMISSIONS'),
              stageCode: role.stageCode || 'N/A',
              employmentType: role.employmentType || 'Contract',
              experience: role.experience || 'N/A',
              location: loc,
              rate: `${role.currency || 'USD'} ${role.minRate || 0} - ${role.maxRate || 0}`,
              progress: 0,
              stepCounts: role.stepCounts || '',
              createdAt: role.createdAt,
            };
          });
          setSubmissions(mapped);
        }
      } catch (err) {
        console.error('Error fetching submissions:', err);
        setError('Failed to load submissions.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [recruiter.id, startDate, endDate, client]);

  const formatTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateRange = () => {
    const s = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const e = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${s} — ${e}`;
  };

  const sourcedCount = submissions.filter(s => s.pipelineCategory === 'SOURCED').length;
  const submittedCount = submissions.filter(s => s.pipelineCategory === 'SUBMISSIONS').length;

  const filteredSubmissions = submissions.filter(s => {
    if (activeTab === 'sourced') return s.pipelineCategory === 'SOURCED';
    if (activeTab === 'submitted') return s.pipelineCategory === 'SUBMISSIONS';
    return true;
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={ev => ev.stopPropagation()} style={{ maxWidth: '1200px', width: '92%' }}>
        {/* Header */}
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Applications & Submissions by {recruiter.name}</h3>
            <p className="modal-subtitle">{formatDateRange()} {client ? `• Client: ${client}` : ''}</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Tab Filters */}
        <div style={{ display: 'flex', gap: '8px', padding: '12px 24px 0', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <button
            onClick={() => setActiveTab('all')}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              borderBottom: activeTab === 'all' ? '2px solid #3b5bfd' : '2px solid transparent',
              color: activeTab === 'all' ? '#3b5bfd' : '#64748b',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            All Applications ({submissions.length})
          </button>
          <button
            onClick={() => setActiveTab('sourced')}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              borderBottom: activeTab === 'sourced' ? '2px solid #6366f1' : '2px solid transparent',
              color: activeTab === 'sourced' ? '#6366f1' : '#64748b',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            👥 Sourced Pre-Client ({sourcedCount})
          </button>
          <button
            onClick={() => setActiveTab('submitted')}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              borderBottom: activeTab === 'submitted' ? '2px solid #3b5bfd' : '2px solid transparent',
              color: activeTab === 'submitted' ? '#3b5bfd' : '#64748b',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            🚀 Submitted to Client ({submittedCount})
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {loading ? (
            <div className="modal-loading">
              <div className="modal-spinner" />
              <p>Loading applications...</p>
            </div>
          ) : error ? (
            <div className="modal-error">{error}</div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="modal-empty">No {activeTab === 'sourced' ? 'sourced' : activeTab === 'submitted' ? 'submitted' : ''} applications found for this recruiter in the selected period.</div>
          ) : (
            <div className="modal-table-wrapper">
              <table className="submissions-table" style={{ fontSize: '13px' }}>
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Category</th>
                    <th>Client</th>
                    <th>External Job Ref</th>
                    <th>Job Title</th>
                    <th>Type</th>
                    <th>Exp</th>
                    <th>Location</th>
                    <th>Rate</th>
                    <th>Technical Progress</th>
                    <th>Applied</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmissions.map((row, idx) => (
                    <tr key={row.submissionId || `${row.id}-${idx}`}>
                      <td>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{row.candidateName}</div>
                        {row.candidateEmail && <div style={{ fontSize: '11px', color: '#64748b' }}>{row.candidateEmail}</div>}
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 700,
                          background: '#eff6ff',
                          color: '#1d4ed8',
                          border: '1px solid #bfdbfe'
                        }}>
                          {row.pipelineCategory === 'SOURCED' ? 'Sourced' : 'Submitted'}
                        </span>
                      </td>
                      <td className="client-name">{row.clientName}</td>
                      <td style={{ fontSize: '13px', fontWeight: '500', color: '#3b5bfd' }}>
                        <Link
                          to={`/submissions/${row.id}`}
                          state={{ jobInfo: row }}
                          className="job-title-link"
                          onClick={onClose}
                        >
                          {row.externalJobRef}
                        </Link>
                      </td>
                      <td>
                        <Link
                          to={`/submissions/${row.id}`}
                          state={{ jobInfo: row }}
                          className="job-title-link"
                          onClick={onClose}
                        >
                          {row.jobTitle}
                        </Link>
                      </td>
                      <td>{row.employmentType}</td>
                      <td>{row.experience}</td>
                      <td className="na-text" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.location}>
                        {row.location}
                      </td>
                      <td>{row.rate}</td>
                      <td>
                        <ProgressChevron
                          counts={row.stepCounts ? row.stepCounts.split(',').map(Number) : []}
                          rejectedCount={row.rejectedCount || 0}
                          withdrawnCount={row.withdrawnCount || 0}
                          hiredCount={row.hiredCount || 0}
                        />
                      </td>
                      <td>{formatTime(row.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && (
          <div className="modal-footer">
            <span>{filteredSubmissions.length} record{filteredSubmissions.length !== 1 ? 's' : ''} found</span>
            <button className="modal-close-footer-btn" onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
// Main BenchReport Component
// ─────────────────────────────────────────
export const BenchReport = () => {
  const today = toYMD(new Date());

  // Filters
  const [quickSelect, setQuickSelect] = useState('today');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);

  // Granularity, Active Filter & Highlights
  const [activeFilter, setActiveFilter] = useState(null);
  const [granularity, setGranularity] = useState('daily');
  const [highlightedRecruiter, setHighlightedRecruiter] = useState(null);
  const [highlightedTotalRecruiter, setHighlightedTotalRecruiter] = useState(null);

  // Data State
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRecruiterModal, setSelectedRecruiterModal] = useState(null);

  // Download Dropdown
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const downloadRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch date-wise Clients
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BASE_URL}/api/bench-manage/clients`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { startDate, endDate }
        });
        if (res.data.success) {
          const fetchedClients = res.data.clients || [];
          setClientOptions(fetchedClients);
          if (selectedClient && fetchedClients.length > 0) {
            const exists = fetchedClients.some(c => (c.clientName || '').toLowerCase() === selectedClient.toLowerCase());
            if (!exists) {
              setSelectedClient(null);
            }
          }
        }
      } catch (err) {
        console.error('Error loading clients:', err);
      }
    };
    fetchClients();
  }, [startDate, endDate]);

  // Fetch Report Data
  const fetchReport = useCallback(async (sDate = startDate, eDate = endDate, client = selectedClient, gran = granularity) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/bench-manage/report`, {
        params: {
          startDate: sDate,
          endDate: eDate,
          client: client || 'all',
          granularity: gran
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setReportData(res.data);
      }
    } catch (err) {
      console.error('Error fetching bench report:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedClient, granularity]);

  useEffect(() => {
    fetchReport(startDate, endDate, selectedClient, granularity);
  }, [fetchReport, startDate, endDate, selectedClient, granularity]);

  // Quick Select Change Handler
  const handleQuickSelectChange = (e) => {
    const val = e.target.value;
    setQuickSelect(val);
    setActiveFilter(null);
    if (val === 'custom') return;

    const now = new Date();
    const todayObj = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let start = todayObj;
    let end = todayObj;

    if (val === 'today') {
      start = todayObj;
      end = todayObj;
    } else if (val === 'yesterday') {
      start = new Date(todayObj.getFullYear(), todayObj.getMonth(), todayObj.getDate() - 1);
      end = new Date(todayObj.getFullYear(), todayObj.getMonth(), todayObj.getDate() - 1);
    } else if (val === 'this_week') {
      const day = todayObj.getDay();
      const diff = todayObj.getDate() - day + (day === 0 ? -6 : 1);
      start = new Date(todayObj.getFullYear(), todayObj.getMonth(), diff);
      end = new Date();
    } else if (val === 'last_week') {
      const day = todayObj.getDay();
      const diff = todayObj.getDate() - day + (day === 0 ? -6 : 1) - 7;
      start = new Date(todayObj.getFullYear(), todayObj.getMonth(), diff);
      end = new Date(todayObj.getFullYear(), todayObj.getMonth(), diff + 6);
    } else if (val === 'this_month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date();
    } else if (val === 'last_month') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
    } else if (val === 'this_quarter') {
      const quarter = Math.floor(now.getMonth() / 3);
      start = new Date(now.getFullYear(), quarter * 3, 1);
      end = new Date();
    } else if (val === 'last_quarter') {
      const quarter = Math.floor(now.getMonth() / 3) - 1;
      start = new Date(now.getFullYear(), quarter * 3, 1);
      end = new Date(now.getFullYear(), (quarter + 1) * 3, 0);
    }

    const sYMD = toYMD(start);
    const eYMD = toYMD(end);
    setStartDate(sYMD);
    setEndDate(eYMD);
    fetchReport(sYMD, eYMD, selectedClient, granularity);
  };

  // Handle Daily / Weekly / Monthly / Yearly granularity buttons matching JobReport functionality
  const handleGranularityChange = (period) => {
    const today = new Date();
    let start = new Date(today);

    if (period === 'daily') {
      start.setDate(today.getDate() - 30); // Last 30 days for Daily View
    } else if (period === 'weekly') {
      start.setMonth(today.getMonth() - 1); // Last 1 month for Weekly View
    } else if (period === 'monthly') {
      start.setFullYear(today.getFullYear() - 1); // Last 1 year for Monthly View
    } else if (period === 'yearly') {
      start.setFullYear(today.getFullYear() - 5); // Last 5 years for Yearly View
    }

    const newStart = toYMD(start);
    const newEnd = toYMD(today);

    setStartDate(newStart);
    setEndDate(newEnd);
    setGranularity(period);
    setActiveFilter(period);
    setQuickSelect('custom');
    fetchReport(newStart, newEnd, selectedClient, period);
  };

  const handleResetFilters = () => {
    const todayYMD = toYMD(new Date());
    setQuickSelect('today');
    setStartDate(todayYMD);
    setEndDate(todayYMD);
    setSelectedClient(null);
    setGranularity('daily');
    setActiveFilter(null);
    setHighlightedRecruiter(null);
    fetchReport(todayYMD, todayYMD, null, 'daily');
  };

  const kpis = reportData?.kpis || {
    totalSubmissions: 0,
    totalOpenJobs: 0,
    topRecruiter: { name: '—', count: 0 },
    topClient: { name: '—', count: 0 }
  };

  const detailedBreakdown = reportData?.detailedBreakdown || [];
  const trendRecruiters = reportData?.trendRecruiters || [];
  const dailyTrendChartData = reportData?.dailyTrendChartData || [];
  const totalTrendRecruiters = reportData?.totalTrendRecruiters || [];
  const totalTrendChartData = reportData?.totalTrendChartData || [];

  // Download Report Options Handler
  const handleDownloadReport = (reportType) => {
    setIsDownloadOpen(false);
    generateBenchReportHtml({
      startDate,
      endDate,
      selectedClient,
      granularity,
      kpis,
      detailedBreakdown,
      clientOptions
    });
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto', fontFamily: 'inherit' }}>
      
      {/* ── Top Header ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <TrendingUp size={26} color="#3b5bfd" />
            Bench Submission Report
          </h1>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
            Submission analytics by recruiter & job coverage
          </p>
        </div>

        {/* Download Dropdown */}
        <div ref={downloadRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setIsDownloadOpen(prev => !prev)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', background: isDownloadOpen ? '#3b5bfd' : '#fff', 
              border: '1px solid #3b5bfd',
              borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, 
              color: isDownloadOpen ? '#fff' : '#3b5bfd',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              transition: 'all 0.15s ease'
            }}
          >
            <Download size={14} /> Download Report <ChevronDown size={14} style={{ transform: isDownloadOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>

          {isDownloadOpen && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', marginTop: '6px',
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15), 0 8px 10px -6px rgba(0,0,0,0.1)', 
              zIndex: 9999, minWidth: '260px', overflow: 'hidden'
            }}>
              <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                Select Report Format
              </div>

              <button
                type="button"
                onClick={() => handleDownloadReport('custom')}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  fontSize: '13px',
                  color: '#1e293b',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f8fafc',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
              >
                <span>⚙️</span>
                <div>
                  <div style={{ fontWeight: '600' }}>Selected Date Range</div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>{startDate} — {endDate}</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleDownloadReport('standard')}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  fontSize: '13px',
                  color: '#1e293b',
                  cursor: 'pointer',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
              >
                <span>📑</span>
                <div>
                  <div style={{ fontWeight: '600' }}>Standard Job Report</div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>Complete job opening &amp; client analytics (PDF/Print)</div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Date Filters & US Client Filter Bar ────────────────────── */}
      <div style={{
        background: '#fff', borderRadius: '8px', padding: '14px 18px',
        marginBottom: '20px', border: '1px solid #e2e8f0',
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Date filters:</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
            
            {/* Quick Select */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Quick Select</label>
              <select
                value={quickSelect}
                onChange={handleQuickSelectChange}
                style={{
                  padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1',
                  outline: 'none', fontSize: '13px', minWidth: '130px', color: '#334155', background: '#fff'
                }}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="last_week">Last Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_quarter">This Quarter</option>
                <option value="last_quarter">Last Quarter</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Start Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Start date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setQuickSelect('custom'); }}
                style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', color: '#334155' }}
              />
            </div>

            {/* End Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>End date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setQuickSelect('custom'); }}
                style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', color: '#334155' }}
              />
            </div>

            {/* Apply Button */}
            <button
              onClick={() => fetchReport(startDate, endDate, selectedClient, granularity)}
              disabled={loading}
              style={{
                padding: '8px 16px', borderRadius: '6px', border: '1px solid #3b5bfd',
                background: '#3b5bfd', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                height: '35px', boxSizing: 'border-box'
              }}
            >
              {loading ? 'Loading...' : 'Apply'}
            </button>

            {/* Searchable US Client Dropdown */}
            <SearchableClientSelect
              clients={clientOptions}
              selectedClient={selectedClient}
              onSelect={(clientName) => {
                setSelectedClient(clientName);
                fetchReport(startDate, endDate, clientName, granularity);
              }}
            />

          </div>
        </div>

        {/* Clear Filters Button */}
        <div>
          {(quickSelect !== 'today' || startDate !== today || endDate !== today || selectedClient !== null) && (
            <button
              onClick={handleResetFilters}
              style={{
                padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0',
                background: '#f8fafc', color: '#64748b', cursor: 'pointer', fontSize: '13px',
                display: 'flex', alignItems: 'center', gap: '4px'
              }}
            >
              <X size={14} /> Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* ── Manager Dashboard Header & Period Switcher ────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: '0 0 2px 0' }}>
            Manager Dashboard
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
            Submission analytics by recruiter & job coverage
          </p>
        </div>

        {/* Period Switcher */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div className="filter-buttons">
            {['daily', 'weekly', 'monthly', 'yearly'].map((period) => (
              <button
                key={period}
                className={`filter-btn ${activeFilter === period ? 'active' : ''}`}
                onClick={() => handleGranularityChange(period)}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── 6 Top KPI Cards (Option 1 Bench Jobs Only) ─────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        
        {/* SOURCED (TOTAL SOURCED) */}
        <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #e5e7eb', borderLeft: '4px solid #6366f1', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#4338ca' }}>SOURCED</span>
            <span style={{ background: '#e0e7ff', color: '#4f46e5', padding: '6px', borderRadius: '8px' }}><Users size={18} /></span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#3730a3' }}>{(kpis.totalSourced || 0) + (kpis.totalSubmissions || 0)}</div>
          <div style={{ fontSize: '12px', color: '#6366f1', marginTop: '4px', fontWeight: 600 }}>Total Sourced</div>
        </div>

        {/* PRE-CLIENT POOL */}
        <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #e5e7eb', borderLeft: '4px solid #8b5cf6', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#6d28d9' }}>PRE-CLIENT POOL</span>
            <span style={{ background: '#ede9fe', color: '#7c3aed', padding: '6px', borderRadius: '8px' }}><Layers size={18} /></span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#5b21b6' }}>{kpis.totalSourced || 0}</div>
          <div style={{ fontSize: '12px', color: '#7c3aed', marginTop: '4px', fontWeight: 600 }}>Internal Pipeline</div>
        </div>

        {/* SUBMISSIONS */}
        <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #e5e7eb', borderLeft: '4px solid #3b5bfd', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e40af' }}>SUBMISSIONS</span>
            <span style={{ background: '#eff6ff', color: '#3b5bfd', padding: '6px', borderRadius: '8px' }}><TrendingUp size={18} /></span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#1e40af' }}>{kpis.totalSubmissions || 0}</div>
          <div style={{ fontSize: '12px', color: '#3b5bfd', marginTop: '4px', fontWeight: 600 }}>Client Submissions</div>
        </div>

        {/* TOTAL OPEN JOBS (Bench Only) */}
        <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>TOTAL OPEN JOBS</span>
            <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '6px', borderRadius: '8px' }}><Briefcase size={18} /></span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#1d4ed8' }}>{kpis.totalOpenJobs || 0}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Bench Jobs (Selected Period)</div>
        </div>

        {/* TOP RECRUITER */}
        <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>TOP RECRUITER</span>
            <span style={{ background: '#fef3c7', color: '#92400e', padding: '6px', borderRadius: '8px' }}><Award size={18} /></span>
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#92400e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {kpis.topRecruiter.name || '—'}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            {kpis.topRecruiter.count || 0} submissions
          </div>
        </div>

        {/* TOP CLIENT */}
        <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>TOP CLIENT</span>
            <span style={{ background: '#ede9fe', color: '#6d28d9', padding: '6px', borderRadius: '8px' }}><Building2 size={18} /></span>
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#6d28d9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {kpis.topClient.name || '—'}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            {kpis.topClient.count || 0} submissions
          </div>
        </div>

      </div>

      {/* ── Trend Chart Section: Client Submissions by Recruiter ──────────── */}
      <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '20px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={18} color="#3b5bfd" />
            Client Submissions by Recruiter — {activeFilter ? activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1) : 'Daily'} View
          </h3>
        </div>

        {dailyTrendChartData.length === 0 ? (
          <div style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontStyle: 'italic', fontSize: '13px' }}>
            No trend data available for the selected period.
          </div>
        ) : (
          <div style={{ width: '100%', height: '320px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyTrendChartData} margin={{ top: 25, right: 30, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="dateStr" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} dy={8} />
                <YAxis 
                  domain={[0, dataMax => Math.max(dataMax + 2, 4)]} 
                  tick={{ fontSize: 11, fill: '#64748b' }} 
                  axisLine={false} 
                  tickLine={false} 
                  allowDecimals={false} 
                />
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  wrapperStyle={{ zIndex: 9999 }}
                  contentStyle={{ background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', fontSize: '12px' }}
                  labelStyle={{ fontWeight: 600, color: '#111827' }}
                />
                <Legend
                  content={() => (
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', paddingTop: '12px' }}>
                      {trendRecruiters.map((recruiter, idx) => {
                        const isActive = !highlightedRecruiter || highlightedRecruiter === recruiter;
                        return (
                          <div
                            key={recruiter}
                            onClick={() => setHighlightedRecruiter(prev => prev === recruiter ? null : recruiter)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                              padding: '3px 8px', borderRadius: '14px',
                              background: highlightedRecruiter === recruiter ? COLORS[idx % COLORS.length] + '20' : 'transparent',
                              border: highlightedRecruiter === recruiter ? `1px solid ${COLORS[idx % COLORS.length]}` : '1px solid transparent',
                              opacity: isActive ? 1 : 0.4, fontSize: '12px', color: '#475569'
                            }}
                          >
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS[idx % COLORS.length] }} />
                            {recruiter}
                          </div>
                        );
                      })}
                      {highlightedRecruiter && (
                        <div
                          onClick={() => setHighlightedRecruiter(null)}
                          style={{ cursor: 'pointer', padding: '3px 8px', borderRadius: '14px', background: '#f1f5f9', fontSize: '11px', color: '#64748b' }}
                        >
                          ✕ Clear
                        </div>
                      )}
                    </div>
                  )}
                />
                {(highlightedRecruiter ? trendRecruiters.filter(r => r === highlightedRecruiter) : trendRecruiters).map((recruiter) => {
                  const idx = trendRecruiters.indexOf(recruiter);
                  return (
                    <Bar
                      key={recruiter}
                      dataKey={recruiter}
                      fill={COLORS[idx % COLORS.length]}
                      radius={[2, 2, 0, 0]}
                      barSize={6}
                      label={
                        highlightedRecruiter 
                          ? { position: 'top', fill: COLORS[idx % COLORS.length], fontSize: 12, fontWeight: 600, dy: -5 } 
                          : false
                      }
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ── Trend Chart Section: Total Sourced by Recruiter ──────────── */}
      <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '20px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={18} color="#4f46e5" />
            Total Sourced by Recruiter — {activeFilter ? activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1) : 'Daily'} View
          </h3>
        </div>

        {totalTrendChartData.length === 0 ? (
          <div style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontStyle: 'italic', fontSize: '13px' }}>
            No trend data available for the selected period.
          </div>
        ) : (
          <div style={{ width: '100%', height: '320px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={totalTrendChartData} margin={{ top: 25, right: 30, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="dateStr" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} dy={8} />
                <YAxis 
                  domain={[0, dataMax => Math.max(dataMax + 2, 4)]} 
                  tick={{ fontSize: 11, fill: '#64748b' }} 
                  axisLine={false} 
                  tickLine={false} 
                  allowDecimals={false} 
                />
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  wrapperStyle={{ zIndex: 9999 }}
                  contentStyle={{ background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', fontSize: '12px' }}
                  labelStyle={{ fontWeight: 600, color: '#111827' }}
                />
                <Legend
                  content={() => (
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', paddingTop: '12px' }}>
                      {totalTrendRecruiters.map((recruiter, idx) => {
                        const isActive = !highlightedTotalRecruiter || highlightedTotalRecruiter === recruiter;
                        return (
                          <div
                            key={recruiter}
                            onClick={() => setHighlightedTotalRecruiter(prev => prev === recruiter ? null : recruiter)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                              padding: '3px 8px', borderRadius: '14px',
                              background: highlightedTotalRecruiter === recruiter ? COLORS[idx % COLORS.length] + '20' : 'transparent',
                              border: highlightedTotalRecruiter === recruiter ? `1px solid ${COLORS[idx % COLORS.length]}` : '1px solid transparent',
                              opacity: isActive ? 1 : 0.4, fontSize: '12px', color: '#475569'
                            }}
                          >
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS[idx % COLORS.length] }} />
                            {recruiter}
                          </div>
                        );
                      })}
                      {highlightedTotalRecruiter && (
                        <div
                          onClick={() => setHighlightedTotalRecruiter(null)}
                          style={{ cursor: 'pointer', padding: '3px 8px', borderRadius: '14px', background: '#f1f5f9', fontSize: '11px', color: '#64748b' }}
                        >
                          ✕ Clear
                        </div>
                      )}
                    </div>
                  )}
                />
                {(highlightedTotalRecruiter ? totalTrendRecruiters.filter(r => r === highlightedTotalRecruiter) : totalTrendRecruiters).map((recruiter) => {
                  const idx = totalTrendRecruiters.indexOf(recruiter);
                  return (
                    <Bar
                      key={recruiter}
                      dataKey={recruiter}
                      fill={COLORS[idx % COLORS.length]}
                      radius={[2, 2, 0, 0]}
                      barSize={6}
                      label={
                        highlightedTotalRecruiter 
                          ? { position: 'top', fill: COLORS[idx % COLORS.length], fontSize: 12, fontWeight: 600, dy: -5 } 
                          : false
                      }
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ── Bottom Split Grid: Total Submission Trend + Detailed Breakdown ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '20px' }}>
        
        {/* Left: Total Submission Trend Area Chart */}
        <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
          <div style={{ padding: '14px 20px', background: '#1e293b', color: '#fff', fontWeight: 700, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TrendingUp size={16} color="#38bdf8" />
            Total Pipeline Activity Trend
          </div>
          <div style={{ padding: '20px' }}>
            {dailyTrendChartData.length === 0 ? (
              <div style={{ height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontStyle: 'italic', fontSize: '13px' }}>
                No data available
              </div>
            ) : (
              <div style={{ width: '100%', height: '260px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart 
                    data={dailyTrendChartData.map(d => {
                      const subs = d.totalSubmissions || 0;
                      const sourced = (d.totalSourced !== undefined && d.totalSubmissions !== undefined && d.totalSourced >= subs)
                        ? d.totalSourced
                        : ((d.totalSourced || 0) + subs);
                      return {
                        ...d,
                        totalSubmissions: subs,
                        totalSourced: sourced
                      };
                    })}
                    margin={{ top: 25, right: 20, left: -20, bottom: 10 }}
                  >
                    <defs>
                      <linearGradient id="totalTrendGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b5bfd" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#3b5bfd" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="sourcedBenchGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="dateStr" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} dy={8} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      wrapperStyle={{ zIndex: 9999 }}
                      contentStyle={{ background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', fontSize: '12px' }}
                    />
                    <Legend verticalAlign="top" height={36} />
                    <Area
                      type="monotone"
                      dataKey="totalSubmissions"
                      name="Submissions"
                      stroke="#3b5bfd"
                      strokeWidth={2.5}
                      fill="url(#totalTrendGradient)"
                      dot={{ r: 4, fill: '#3b5bfd', strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 6, fill: '#3b5bfd', stroke: '#fff', strokeWidth: 2 }}
                    />
                    <Area
                      type="monotone"
                      dataKey="totalSourced"
                      name="Sourced"
                      stroke="#6366f1"
                      strokeWidth={2}
                      strokeDasharray="4 4"
                      fill="url(#sourcedBenchGradient)"
                      dot={{ r: 3, fill: '#6366f1', strokeWidth: 1.5, stroke: '#fff' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Right: Detailed Breakdown Table */}
        <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: 700, fontSize: '15px', color: '#111827' }}>
            Detailed Breakdown
          </div>
          <div style={{ overflowX: 'auto', maxHeight: '330px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>
                  <th style={{ padding: '10px 16px' }}>Recruiter</th>
                  <th style={{ padding: '10px 16px' }}>Team Pod</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center' }}>Pre-Client Pool</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center' }}>Client Submissions</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '30px', textAlign: 'center', color: '#6b7280' }}>
                      Loading data...
                    </td>
                  </tr>
                ) : detailedBreakdown.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '30px', textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }}>
                      No data available
                    </td>
                  </tr>
                ) : (
                  detailedBreakdown.map((r, idx) => (
                    <tr
                      key={idx}
                      style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                    >
                      <td style={{ padding: '10px 16px', fontWeight: 600, color: '#111827' }}>
                        <button
                          onClick={() => setSelectedRecruiterModal(r)}
                          style={{
                            background: 'none', border: 'none', color: '#3b5bfd',
                            fontWeight: 600, fontSize: '13px', cursor: 'pointer', textAlign: 'left', padding: 0
                          }}
                        >
                          {r.name}
                        </button>
                      </td>
                      <td style={{ padding: '10px 16px', color: '#64748b' }}>
                        {r.team || 'Bench Sales'}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                        <span style={{
                          background: '#eff6ff',
                          color: '#1d4ed8',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 700,
                          border: '1px solid #bfdbfe',
                          display: 'inline-block'
                        }}>
                          {r.sourced || 0}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                        <button
                          onClick={() => setSelectedRecruiterModal(r)}
                          style={{
                            padding: '3px 10px', borderRadius: '12px', border: '1px solid #3b5bfd',
                            background: '#eff6ff', color: '#3b5bfd', fontWeight: 700, fontSize: '12px', cursor: 'pointer'
                          }}
                        >
                          {r.submissions || 0}
                        </button>
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 700, color: '#334155' }}>
                        {r.total || (r.sourced || 0) + (r.submissions || 0)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* ── Submissions Detail Modal (Job Submission Exact Style) ─────── */}
      {selectedRecruiterModal && (
        <SubmissionsModal
          recruiter={selectedRecruiterModal}
          startDate={startDate}
          endDate={endDate}
          client={selectedClient}
          onClose={() => setSelectedRecruiterModal(null)}
        />
      )}

    </div>
  );
};

export default BenchReport;
